"use client";

import Image from "next/image";
import { memo, useMemo, useState, useRef, useCallback, type ChangeEvent } from "react";
import type { CSSProperties } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, formatTokenIntelligent } from "@/utils";

export interface TokenOption {
  value: string;
  label: string;
  icon: string;
  iconAlt?: string;
}

interface TokenAmountInputProps {
  label: string;
  amount: string;
  onAmountChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onAmountFocus?: () => void;
  onAmountBlur?: () => void;
  token: string;
  tokenOptions: TokenOption[];
  onTokenChange?: (value: string) => void;
  quickActions?: { label: string; value: number; disabled?: boolean }[];
  onQuickSelect?: (ratio: number) => void;
  quickActionsDisabled?: boolean;
  balance?: bigint | null;
  balanceDecimals?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnlyToken?: boolean;
}

const gradientStyle: CSSProperties = {
  background: "linear-gradient(135deg, #0a0a0b, #141416)",
  boxShadow:
    "inset 0 6px 12px rgba(0, 0, 0, 0.9), inset 0 -1px 0 rgba(255, 255, 255, 0.03), inset 0 1px 2px rgba(0, 0, 0, 1)",
  border: "2px solid rgba(255, 255, 255, 0.12)",
};

const MIN_TRIGGER_REM = 8;
const MAX_TRIGGER_REM = 16;
const ICON_PADDING_REM = 4.4;
const CHARACTER_REM = 0.8;

// Font size scaling constants
const MAX_FONT_SIZE = 32; // Default/maximum font size in pixels
const MIN_FONT_SIZE = 14; // Minimum font size in pixels - smaller to fit more
const FONT_SCALE_START = 15; // Start scaling after this many characters - allow more at full size
const FONT_SCALE_END = 45; // Stop scaling at this many characters - much longer range

/**
 * Calculates dynamic font size based on input length
 * Smoothly scales from MAX_FONT_SIZE to MIN_FONT_SIZE
 */
const calculateFontSize = (value: string): number => {
  const length = value.length;

  // Return max size for short inputs
  if (length <= FONT_SCALE_START) {
    return MAX_FONT_SIZE;
  }

  // Return min size for long inputs
  if (length >= FONT_SCALE_END) {
    return MIN_FONT_SIZE;
  }

  // Calculate scaled size for intermediate lengths
  // Linear interpolation between max and min based on position
  const progress = (length - FONT_SCALE_START) / (FONT_SCALE_END - FONT_SCALE_START);
  const fontSize = MAX_FONT_SIZE - (progress * (MAX_FONT_SIZE - MIN_FONT_SIZE));

  return Math.round(fontSize);
};


export const TokenAmountInput = memo(
  ({
    label,
    amount,
    onAmountChange,
    onAmountFocus,
    onAmountBlur,
    token,
    tokenOptions,
    onTokenChange,
    quickActions = [
      { label: "25%", value: 0.25 },
      { label: "50%", value: 0.5 },
      { label: "75%", value: 0.75 },
      { label: "100%", value: 1 },
    ],
    onQuickSelect,
    quickActionsDisabled = false,
    balance,
    balanceDecimals = 18,
    className,
    placeholder = "0.0",
    disabled = false,
    readOnlyToken = false,
  }: TokenAmountInputProps) => {
    // Track focus state internally
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const selected = useMemo(
      () => tokenOptions.find((option) => option.value === token) ?? tokenOptions[0],
      [token, tokenOptions],
    );

    const formattedBalance = useMemo(() => {
      if (!balance) return null;
      return formatTokenIntelligent(balance, balanceDecimals, true);
    }, [balance, balanceDecimals]);

    // Calculate dynamic font size based on amount length
    const fontSize = useMemo(() => {
      return calculateFontSize(amount || '');
    }, [amount]);

    // Always show full value - no truncation
    const displayValue = amount;

    // Handle focus event - update internal state and call parent callback
    const handleFocus = useCallback(() => {
      setIsFocused(true);
      onAmountFocus?.();

      // Ensure cursor stays visible by scrolling to end after focus
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
          inputRef.current.scrollLeft = inputRef.current.scrollWidth;
        }
      });
    }, [onAmountFocus]);

    // Handle blur event - update internal state and call parent callback
    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onAmountBlur?.();
    }, [onAmountBlur]);

    // Handle input change - ensure cursor stays visible
    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      onAmountChange(event);

      // Keep cursor in view while typing
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const cursorPosition = inputRef.current.selectionStart || 0;
          const inputWidth = inputRef.current.clientWidth;
          const scrollWidth = inputRef.current.scrollWidth;

          // If content overflows, adjust scroll to keep cursor visible
          if (scrollWidth > inputWidth) {
            // Calculate approximate position of cursor
            const charWidth = scrollWidth / inputRef.current.value.length;
            const cursorX = cursorPosition * charWidth;

            // Scroll to keep cursor in view with some padding
            if (cursorX > inputRef.current.scrollLeft + inputWidth - 50) {
              inputRef.current.scrollLeft = cursorX - inputWidth + 100;
            } else if (cursorX < inputRef.current.scrollLeft + 50) {
              inputRef.current.scrollLeft = Math.max(0, cursorX - 50);
            }
          }
        }
      });
    }, [onAmountChange]);

    const showDropdown = Boolean(
      !readOnlyToken && onTokenChange && tokenOptions.length > 1,
    );

    const longestLabelLength = useMemo(
      () => tokenOptions.reduce((acc, option) => Math.max(acc, option.label.length), 0),
      [tokenOptions],
    );

    const triggerWidthRem = useMemo(() => {
      const selectedLength = selected?.label?.length ?? 0;
      const effectiveLength = Math.max(longestLabelLength, selectedLength);
      const estimated = ICON_PADDING_REM + effectiveLength * CHARACTER_REM;
      const clamped = Math.min(MAX_TRIGGER_REM, Math.max(MIN_TRIGGER_REM, estimated));
      return Number(clamped.toFixed(2));
    }, [longestLabelLength, selected?.label]);

    const selectContainerStyle = useMemo(
      () => {
        // For read-only tokens, let it size to content with constraints
        if (!showDropdown) {
          return {
            width: 'auto',
            minWidth: `${MIN_TRIGGER_REM}rem`,
            maxWidth: `${MAX_TRIGGER_REM}rem`,
          };
        }
        // For dropdowns, use calculated width
        return {
          width: `${triggerWidthRem}rem`,
          minWidth: `${MIN_TRIGGER_REM}rem`,
          maxWidth: `${MAX_TRIGGER_REM}rem`,
        };
      },
      [triggerWidthRem, showDropdown],
    );

    return (
      <div className={cn("space-y-2", className)}>
        <div className="flex items-center justify-between">
          <label
            className="font-pressStart2P text-[10px] uppercase text-white/60"
            style={{ letterSpacing: "0.1em" }}
          >
            {label}
          </label>
          {quickActions.length > 0 && onQuickSelect ? (
            <div className="flex items-center gap-2">
              {quickActions.map((action) => (
                <Button
                  key={action.label}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onQuickSelect(action.value)}
                  disabled={quickActionsDisabled || action.disabled}
                  className="h-6 rounded-sm border border-white/10 bg-white/5 px-2 text-xs text-white/60 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <div className="relative overflow-hidden rounded-xl" style={gradientStyle}>
            <div className={cn(
              "relative z-10 flex gap-5 px-5 py-5",
              formattedBalance ? "items-start" : "items-center"
            )}>
              <Input
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={cn(
                  "flex-1 border-0 bg-transparent px-0 leading-none font-pressStart2P uppercase tracking-[0.08em] text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:ring-offset-0",
                  formattedBalance ? "pt-1" : ""
                )}
                style={{
                  fontSize: `${fontSize}px`,
                  transition: 'font-size 0.2s cubic-bezier(0.4, 0, 0.2, 1)' // Smooth transition for font size changes
                }}
              />
              <div
                className="flex flex-shrink-0 flex-col items-end gap-2"
                style={selectContainerStyle}
              >
                {showDropdown ? (
                  <Select value={selected?.value} onValueChange={onTokenChange}>
                    <SelectTrigger className="flex w-full flex-shrink-0 items-center gap-2 rounded-lg border border-white/10 bg-gradient-to-b from-[#262628] to-[#1a1a1c] px-3 py-2 font-pressStart2P text-[11px] uppercase tracking-[0.06em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_4px_rgba(0,0,0,0.35)] focus:outline-none focus:ring-1 focus:ring-white/20 focus:ring-offset-0">
                      <SelectValue>
                        <div className="flex items-center gap-2 truncate">
                          <Image
                            src={selected?.icon ?? "/token/henlo.webp"}
                            alt={selected?.iconAlt ?? selected?.label ?? ""}
                            width={24}
                            height={24}
                            className="rounded-full"
                          />
                          <span className="truncate">{selected?.label ?? "Select"}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border border-white/10 bg-[#1a1a1c] p-1 text-white shadow-[0_8px_20px_rgba(0,0,0,0.8)]">
                      {tokenOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="m-1 rounded-lg py-2 pl-12 pr-4 font-pressStart2P text-[11px] uppercase tracking-[0.06em] text-white/90 transition-colors hover:bg-white/10 hover:text-white data-[state=checked]:bg-white/10 data-[state=checked]:text-white"
                        >
                          <div className="flex items-center gap-3">
                            <Image
                              src={option.icon}
                              alt={option.iconAlt ?? option.label}
                              width={24}
                              height={24}
                              className="rounded-full"
                            />
                            <span className="truncate">{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-[#1a1a1c] px-3 py-2 font-pressStart2P text-[11px] uppercase tracking-[0.06em] text-white">
                    {selected?.icon ? (
                      <Image
                        src={selected.icon}
                        alt={selected.iconAlt ?? selected.label}
                        width={24}
                        height={24}
                        className="flex-shrink-0 rounded-full"
                      />
                    ) : null}
                    <span className="whitespace-nowrap">{selected?.label}</span>
                  </div>
                )}

                {formattedBalance ? (
                  <div className="text-[8px] font-pressStart2P uppercase tracking-[0.06em] text-white/40">
                    {formattedBalance}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

TokenAmountInput.displayName = "TokenAmountInput";

export default TokenAmountInput;
